import { useCallback, useRef, useState } from "react";
import { addCommentsForSingleProduct, dataRetriveWhenPageRefresh, updateJobs, updateTaskStatus } from "../../../config/Services";
import { assertKitchenResponse, performKitchenAction } from "./kitchenActions";
import { findTaskCartProduct, getCartProducts, preparationNoteKey } from "./preparationComments";
import { useSnackbar } from "../product/hooks/SnackbarProvider";

export default function useKitchenWorkloadActions({ locationId, updatedBy, updatedByName, getFreshJobs, onUpdated }) {
  const showSnackbar = useSnackbar();
  const refreshRef = useRef(onUpdated);
  refreshRef.current = onUpdated;
  const writeRef = useRef(false);
  const readRef = useRef(false);
  const notesRequest = useRef(0);
  const [busy, setBusy] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState("");
  const [notes, setNotes] = useState({});
  const [commentTarget, setCommentTarget] = useState(null);
  const [commentError, setCommentError] = useState("");

  const run = useCallback(async (kind, entries) => {
    if (writeRef.current) throw new Error("A kitchen update is already in progress.");
    writeRef.current = true;
    setBusy(true);
    try {
      const jobs = await getFreshJobs();
      const result = await performKitchenAction({
        kind, entries, jobs, updateJobs, updateTaskStatus,
        audit: { locationId, updatedBy, updatedByName },
      });
      await refreshRef.current();
      if (result.failed.length) {
        throw new Error(`${result.succeeded.length} orders updated; failed orders: ${result.failed.join(", ")}. Retry only the remaining items.`);
      }
      showSnackbar(kind === "start" ? "Selected orders moved to processing" : "Selected items marked ready", "success");
      if (result.skipped) showSnackbar(`${result.skipped} changed item lines were skipped.`, "warning");
      return result;
    } catch (err) {
      showSnackbar(err.message || "Kitchen update failed", "error");
      throw err;
    } finally {
      writeRef.current = false;
      setBusy(false);
    }
  }, [getFreshJobs, locationId, updatedBy, updatedByName, showSnackbar]);
  const onStart = useCallback((entries) => run("start", entries), [run]);
  const onReady = useCallback((entries) => run("ready", entries), [run]);

  const loadNotes = useCallback(async (jobs) => {
    const requestId = ++notesRequest.current;
    if (!jobs.length) { setNotesError(""); setNotesLoading(false); return; }
    setNotesLoading(true);
    setNotesError("");
    let noteJobs = jobs;
    if (jobs.some((job) => job.aggregated)) {
      try {
        const freshJobs = await getFreshJobs();
        noteJobs = jobs.map((job) => job.aggregated
          ? freshJobs.find((fresh) => String(fresh.jobId) === String(job.jobId)) || job : job);
      } catch (err) {
        if (requestId === notesRequest.current) {
          setNotesError(err?.message || "Saved comments could not be loaded.");
          setNotesLoading(false);
        }
        return;
      }
    }
    const carts = new Map();
    const values = {};
    const failures = [];
    await Promise.all(noteJobs.map(async (job) => {
      const cartId = job.metadata?.cartId;
      if (!cartId) return; // Existing job notes remain visible for cartless external orders.
      try {
        if (!carts.has(cartId)) carts.set(cartId, dataRetriveWhenPageRefresh(cartId).then(getCartProducts));
        const products = await carts.get(cartId);
        (job.tasks || []).forEach((task) => {
          const product = findTaskCartProduct(task, job, products);
          if (product) values[preparationNoteKey(job.jobId, task.taskId)] = product.comments || "";
        });
      } catch (_) { failures.push(job.jobId); }
    }));
    if (requestId === notesRequest.current) {
      setNotes((previous) => ({ ...previous, ...values }));
      setNotesError(failures.length ? `Saved comments could not be loaded for jobs ${failures.join(", ")}. Showing the existing kitchen notes.` : "");
      setNotesLoading(false);
    }
  }, [getFreshJobs]);

  const openComment = useCallback(async (job, task) => {
    if (readRef.current || writeRef.current) return;
    readRef.current = true;
    setNotesLoading(true);
    try {
      if (job.aggregated) {
        const fresh = (await getFreshJobs()).find((value) => String(value.jobId) === String(job.jobId));
        const freshTask = fresh?.tasks?.find((value) => String(value.taskId) === String(task.taskId));
        if (!fresh || !freshTask) throw new Error("This kitchen item has changed. Please reopen its details.");
        job = fresh;
        task = freshTask;
      }
      if (!job.metadata?.cartId) throw new Error("This kitchen order has no linked cart for saving preparation comments.");
      const products = getCartProducts(await dataRetriveWhenPageRefresh(job.metadata.cartId));
      const product = findTaskCartProduct(task, job, products);
      if (!product) throw new Error("This item could not be matched safely to its cart product. No comment was changed.");
      setCommentError("");
      setCommentTarget({ job, task, product });
    } catch (err) {
      setNotesError(err.message);
      showSnackbar(err.message, "error");
    } finally { readRef.current = false; setNotesLoading(false); }
  }, [showSnackbar, getFreshJobs]);

  const saveComment = useCallback(async (comment) => {
    if (writeRef.current || !commentTarget) return;
    writeRef.current = true;
    setBusy(true);
    setCommentError("");
    try {
      const { job, task, product } = commentTarget;
      assertKitchenResponse(await addCommentsForSingleProduct({
        cartId: job.metadata.cartId, cartProductId: product.cartProductId,
        comments: comment, updatedById: updatedBy, updatedByName,
      }));
      // Read back the authoritative cart so a successful HTTP response alone
      // cannot make an unsaved note look saved.
      const products = getCartProducts(await dataRetriveWhenPageRefresh(job.metadata.cartId));
      const saved = products.find((item) => item.cartProductId === product.cartProductId);
      if (!saved || (saved.comments || "").trim() !== comment) {
        throw new Error("The saved comment could not be confirmed. Please retry.");
      }
      setNotes((previous) => ({ ...previous, [preparationNoteKey(job.jobId, task.taskId)]: saved.comments || "" }));
      setCommentTarget(null);
      await refreshRef.current();
      showSnackbar("Preparation comment saved", "success");
    } catch (err) {
      setCommentError(err.message);
      showSnackbar(err.message || "Unable to save preparation comment", "error");
    } finally { writeRef.current = false; setBusy(false); }
  }, [commentTarget, updatedBy, updatedByName, showSnackbar]);

  return { busy, onStart, onReady, notes, notesLoading, notesError, loadNotes,
    openComment, commentTarget, closeComment: () => { if (!writeRef.current) setCommentTarget(null); },
    saveComment, commentError };
}
